import { analytics, EVENT_NAMES } from './Analytics/Analytics'
import { productTypeState } from './ProductTypeState'
import { logger } from './logger'
import { sleep } from '../utils/utils'
import axios from 'axios'
import { v4 as uuid } from 'uuid'
import { extractErrorMessage } from '../utils/ApiUtils'
import { Semaphore } from 'async-mutex'
import ExifReader from 'exifreader'
import z from 'zod'
import { API_METHODS, callApi, createApiFunction } from '../api/apiProvider'

const SignedPhotoURLsSchema = z.object({
  uploadURL: z.string(),
  verifyURL: z.string(),
  previewMinUploadURL: z.string(),
  previewHighUploadURL: z.string(),
  downloadURL: z.string(),
})

export type PhotoPreviewBlobs = { min: Blob; high: Blob }

const VERIFY_ATTEMPTS = 3
const VERIFY_RETRY_DELAY_MS = 1000

// The presigned HEAD has no unsigned fallback on a private bucket, so a transient failure is retried here rather
// than failing the upload and uploading the whole file again
async function headUploadedPhoto(verifyURL: string, attempt: number = 1) {
  try {
    return await axios.head(verifyURL)
  } catch (e) {
    if (attempt >= VERIFY_ATTEMPTS) throw e
    await sleep(VERIFY_RETRY_DELAY_MS)
    return headUploadedPhoto(verifyURL, attempt + 1)
  }
}

export async function uploadToS3(
  file: File,
  contentType = 'image/jpeg',
  shouldUseAlternativeUpload = false,
  width?: number,
  height?: number,
  analyticsProps?: Record<string, unknown>,
  timeout?: number,
  previews?: PhotoPreviewBlobs
) {
  const preUpload = +new Date()

  // callApi attaches the login token, so the backend records the account as an owner of the upload
  const data = await callApi(
    createApiFunction(API_METHODS.GET, `/v1/photos/getSignedURL?cb=${uuid()}`)
  )
  const {
    uploadURL,
    verifyURL,
    previewMinUploadURL,
    previewHighUploadURL,
    downloadURL,
  } = SignedPhotoURLsSchema.parse(data)

  const postMixtilesURL = +new Date()
  if (shouldUseAlternativeUpload) {
    const arrayBuffer = await file.arrayBuffer()
    await axios.put(uploadURL, arrayBuffer, {
      headers: { 'Content-Type': contentType },
      timeout,
    })
    logger.info('Uploaded file using arrayBuffer', {
      Length: arrayBuffer.byteLength,
    })
  } else {
    await axios.put(uploadURL, file, {
      headers: { 'Content-Type': contentType },
      timeout,
    })
  }
  if (previews) {
    // The backend builds the previews a photo is missing, so a failed preview doesn't fail the upload
    try {
      await Promise.all([
        axios.put(previewMinUploadURL, previews.min, {
          headers: { 'Content-Type': 'image/jpeg' },
          timeout,
        }),
        axios.put(previewHighUploadURL, previews.high, {
          headers: { 'Content-Type': 'image/jpeg' },
          timeout,
        }),
      ])
    } catch (e) {
      logger.warning('Failed uploading photo previews', {
        message: extractErrorMessage(e),
      })
    }
  }
  // Verify against the same host that accepted the PUT — corporate proxies
  // may block s3.amazonaws.com while allowing the accelerate endpoint, which failed uploads that actually succeeded.
  // A private bucket rejects unsigned HEADs, so the presigned verifyURL is the only one that works there.
  const response = await headUploadedPhoto(verifyURL)

  let imageHasExifData = false
  try {
    await ExifReader.load(file)
    imageHasExifData = true
  } catch (_) {}

  const postUpload = +new Date()
  const eventProperties = {
    'Total Upload Time': postUpload - preUpload,
    'File Type': file.type,
    Path: file.name,
    'S3 Upload Time': postUpload - postMixtilesURL,
    'Image Size': file.size,
    'Image Size (MB)': file.size / 1_000_000,
    Uploader: shouldUseAlternativeUpload ? 'Axios ArrayBuffer' : 'Axios',
    'Content Length': response.headers['content-length'],
    'Converted From HEIC': !!file.convertedFromHeic,
    'Content Type': contentType,
    'Image Width': width,
    'Image Height': height,
    'Megapixels (MP)': width && height ? (width * height) / 1_000_000 : null,
    'Product Type': productTypeState.getProductType(),
    'Has EXIF Data': imageHasExifData,
    ...analyticsProps,
  }
  analytics.track(EVENT_NAMES.PHOTO_UPLOADED, eventProperties)

  const isFileEmpty = response.headers['content-length'] === '0'
  if (isFileEmpty) {
    const fileText = await file.text()
    analytics.track(EVENT_NAMES.EMPTY_PHOTO_UPLOADED, {
      ...eventProperties,
      Length: fileText.length,
    })
    throw new Error('Empty photo uploaded')
  }

  return downloadURL
}

// Limit the number of concurrent uploads to prevent bloating the network bandwidth
const UPLOAD_BATCH_SIZE = 5
const UPLOAD_TIMEOUT = 120000
const uploadSemaphore = new Semaphore(UPLOAD_BATCH_SIZE)

export async function uploadPhotoWithRetries({
  file,
  width,
  height,
  retriesLeft = 5,
  retriesNum = 0,
  contentType = 'image/jpeg',
  analyticsProps,
  previews,
}: {
  file: File
  width?: number
  height?: number
  retriesLeft?: number
  retriesNum?: number
  contentType?: string
  analyticsProps?: Record<string, unknown>
  previews?: PhotoPreviewBlobs
}): Promise<string> {
  let uploadPhotoUrl = null
  try {
    // Use fetch instead of axios on the last two tries to make sure the problem is not axios
    const shouldUseAlternativeUpload = retriesLeft <= 1
    const useTimeout = retriesLeft >= 4
    uploadPhotoUrl = await uploadSemaphore.runExclusive(() =>
      uploadToS3(
        file,
        contentType,
        shouldUseAlternativeUpload,
        width,
        height,
        analyticsProps,
        useTimeout ? UPLOAD_TIMEOUT : undefined,
        previews
      )
    )

    if (retriesNum > 0) {
      analytics.track('Photo Uploaded With Retries', {
        Retries: retriesNum,
      })
    }

    return uploadPhotoUrl
  } catch (e) {
    const message = extractErrorMessage(e)
    if (retriesLeft === 0) {
      logger.error('failed uploading photo', e, { message })
      analytics.track(EVENT_NAMES.PHOTO_UPLOAD_FAILED, {
        Error: message,
        Path: file.name,
        'File Type': file.type,
        'Image Size': file.size,
        'Image Size (MB)': file.size / 1_000_000,
        'Image Width': width,
        'Image Height': height,
        'Megapixels (MP)':
          width && height ? (width * height) / 1_000_000 : null,
      })
      throw e
    } else {
      logger.warning(`Error uploading file retry ${retriesNum}`, { message })
      await sleep(3000)
      return uploadPhotoWithRetries({
        file,
        width,
        height,
        retriesLeft: retriesLeft - 1,
        retriesNum: retriesNum + 1,
        contentType,
        analyticsProps,
        previews,
      })
    }
  }
}
